import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  IAvatar3d,
  IMoodleEnv,
  IOrganization,
  ISource,
  IUser,
  IUserPreferences,
} from "../../../model/model";
import axios from "axios";
import type { AxiosResponse } from "axios";
import apiUrls from "../../../api";
import {
  getAuthFromLocalStorage,
  patchStoredAuthUser,
  storeAuthInLocalStorage,
} from "../../../common/util/localStorage";
import { isTokenExpired } from "../../../common/util/auth";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";
import { omitStrapiEntityMetaKeys, resolveDocumentId } from "../../../common/util/strapiDocumentId";
import { getUserPreferences } from "../../../common/util/getUserPreferences";
import { mergePatchObject } from "../../../common/util/applyMergePatch";

export interface IProfile {
  currentUser?: IUser;
  organization?: IOrganization;
  avatar3d: IAvatar3d;
  moodleEnv?: IMoodleEnv;
  preferencesResyncing?: boolean;
}

export interface INewPassword {
  email?: string;
  password?: string;
  passwordConfirmation?: string;
}
export interface ICheckEmail {
  email: string;
  id?: number;
}

interface IStrapiValidationErrorPayload {
  message: Array<{ messages: Array<{ message: string }> }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUnknownArray(value: unknown): value is Array<unknown> {
  return Array.isArray(value);
}

function isStrapiValidationErrorPayload(data: unknown): data is IStrapiValidationErrorPayload {
  return (
    isRecord(data) &&
    isUnknownArray(data.message) &&
    data.message.length > 0 &&
    isRecord(data.message[0]) &&
    isUnknownArray(data.message[0].messages) &&
    data.message[0].messages.length > 0 &&
    isRecord(data.message[0].messages[0]) &&
    typeof data.message[0].messages[0].message === "string"
  );
}

type MergePatch<T> = T extends readonly unknown[]
  ? T
  : T extends object
  ? { [K in keyof T]?: MergePatch<T[K]> | null }
  : T;

export type IUserPreferencesPatch = {
  [K in keyof IUserPreferences]?: MergePatch<IUserPreferences[K]> | null;
};

const getRememberedAuth = () => {
  const auth = getAuthFromLocalStorage();
  if (auth.user && auth.organization && auth.jwt && auth.isRemebered && !isTokenExpired(auth.jwt)) {
    return { jwt: auth.jwt, helpDocsJwt: auth.helpDocsJwt ?? "", isRemebered: auth.isRemebered };
  }
  return undefined;
};

export const rememberPreferences = (preferences: IUserPreferences) => {
  if (getRememberedAuth()) {
    patchStoredAuthUser({ preferences });
  }
};

export const updateUser = createAsyncThunk(
  "profile/updateUser",
  async (
    user: (IUser | (Record<string, unknown> & { id: number })) & IAppLoaderAction,
    { getState },
  ) => {
    const id = user?.id;
    let response: IUser | undefined;

    await axios
      .put<IUser>(`${apiUrls.register.users}/${id}`, {
        data: { ...user },
      })
      .then((res) => {
        if (res !== undefined) {
          const localPreferences =
            "preferences" in user
              ? undefined
              : (getState() as RootState).profile.currentUser?.preferences;
          response = localPreferences ? { ...res.data, preferences: localPreferences } : res.data;

          const rememberedAuth = getRememberedAuth();
          if (rememberedAuth) {
            void storeAuthInLocalStorage(
              response,
              res.data.organization,
              rememberedAuth.jwt,
              rememberedAuth.helpDocsJwt,
              rememberedAuth.isRemebered,
            );
          }
        }
      })
      .catch((e: unknown) => {
        console.error(e);
        return e;
      });

    return response;
  },
);

export const patchUserPreferences = createAsyncThunk(
  "profile/patchUserPreferences",
  async (patch: IUserPreferencesPatch) => {
    const res = await axios.patch<{ preferences: IUserPreferences }>(
      apiUrls.register.mePreferences,
      patch,
    );
    rememberPreferences(res.data.preferences);
    return res.data.preferences;
  },
);

export const fetchUserPreferences = createAsyncThunk(
  "profile/fetchUserPreferences",
  async (_options: { resync?: boolean }) => {
    const res = await axios.get<IUser>(`${apiUrls.register.users}/me`);
    return getUserPreferences({ currentUser: res.data }) ?? {};
  },
);

export const syncUserWithServer = createAsyncThunk(
  "profile/syncUserWithServer",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    const authFromLocalStorage = getAuthFromLocalStorage();
    const userId = authFromLocalStorage?.user?.id;

    const res = await axios.get(`${apiUrls.register.users}/${userId}?populate=*`);
    return res.data as IUser;
  },
);

export const setCurrentUserAndOrg = createAsyncThunk(
  "profile/setCurrentUserAndOrg",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    let organization: IOrganization | undefined;
    let currentUser: IUser | undefined;
    const authFromLocalStorage = getAuthFromLocalStorage();
    if (
      authFromLocalStorage.user &&
      authFromLocalStorage.organization &&
      authFromLocalStorage.jwt &&
      authFromLocalStorage.isRemebered &&
      authFromLocalStorage.jwt !== null &&
      !isTokenExpired(authFromLocalStorage.jwt)
    ) {
      currentUser = authFromLocalStorage.user;
      organization = authFromLocalStorage.organization;
    } else {
      await axios
        .get<IUser>(
          `${apiUrls.register.users}/me?populate[role]=true&populate[source_avatar]=true&populate[preferred_language]=true&populate[user_opened_tutorial]=true&populate[active_gpt_conversation]=true&populate[organization][populate][admin]=true&populate[organization][populate][source_logo]=true`,
        )
        .then((res) => {
          currentUser = res.data;

          organization = res.data.organization;
        })
        .catch((e: unknown) => {
          console.error(e);
          return e;
        });
    }

    return { currentUser, organization };
  },
);

export const updateOrganization = createAsyncThunk(
  "auth/updateOrganization",
  async (organization: IOrganization & IAppLoaderAction) => {
    const idOrganization = organization?.id;
    const documentId = await resolveDocumentId(
      apiUrls.register.organization,
      idOrganization,
      organization,
    );
    let response: IOrganization | undefined;
    await axios
      .put<IOrganization>(
        `${apiUrls.register.organization}/${documentId}?populate[admin]=true&populate[source_logo]=true`,
        omitStrapiEntityMetaKeys(
          {
            ...organization,
            admin: organization.admin?.id,
            source_logo: organization.source_logo?.id,
          },
          ["componentId"],
        ),
      )
      .then((res) => {
        response = { ...res.data, admin: res.data.admin };

        const authFromLocalStorage = getAuthFromLocalStorage();
        if (
          authFromLocalStorage.user &&
          authFromLocalStorage.organization &&
          authFromLocalStorage.jwt &&
          authFromLocalStorage.isRemebered &&
          authFromLocalStorage.jwt !== null &&
          !isTokenExpired(authFromLocalStorage.jwt)
        ) {
          const temp: IOrganization = { ...res.data, admin: res.data.admin };

          void storeAuthInLocalStorage(
            authFromLocalStorage.user,
            temp,
            authFromLocalStorage.jwt,
            authFromLocalStorage.helpDocsJwt ?? "",
            authFromLocalStorage.isRemebered,
          );
        }
      })
      .catch((e: unknown) => {
        console.error(e);
        return e;
      });
    return response;
  },
);

export const checkPassword = createAsyncThunk(
  "profile/checkPassword",
  async ({ password }: { password: string } & IAppLoaderAction) => {
    let response: number | undefined;

    await axios
      .post<number>(apiUrls.auth.checkPwd, {
        password,
      })
      .then((res) => {
        if (res !== undefined) {
          response = res.data;
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return response;
  },
);

export const checkEmail = createAsyncThunk(
  "profile/checkEmail",
  async (user: ICheckEmail & IAppLoaderAction) => {
    const email = user.email;
    const id = user.id;
    let response: boolean | undefined;

    await axios
      .post<boolean>(`${apiUrls.auth.checkEmail}/${id}`, {
        email,
      })
      .then((res) => {
        if (res !== undefined) {
          response = res.data;
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return response;
  },
);

export const changePassword = createAsyncThunk(
  "profile/changePassword",
  async (data: INewPassword & IAppLoaderAction) => {
    let response: AxiosResponse<{ message: string }> | { error: string } | undefined;
    await axios
      .post<{ message: string }>(apiUrls.auth.changePasswordFromProfile, {
        email: data.email,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      })
      .then((res) => {
        response = res;
      })
      .catch((error: unknown) => {
        if (axios.isAxiosError(error) && isStrapiValidationErrorPayload(error.response?.data)) {
          const message = error.response.data.message[0].messages[0].message;
          console.error(message);
          response = { error: message };
        } else {
          console.error(error);
          response = { error: "An error occured" };
        }
      });

    return response;
  },
);

export const changeHasAcceptedCGV = createAsyncThunk(
  "profile/changeHasAcceptedCGV",
  async ({ userId }: { userId?: number } & IAppLoaderAction) => {
    return await axios
      .put<IUser>(`${apiUrls.register.users}/${userId}`, { has_accepted_cgv: 1 })
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const fetchMoodleEnv = createAsyncThunk(
  "profile/fetchMoodleEnv",
  async ({ organizationId }: { organizationId: number } & IAppLoaderAction) => {
    return await axios
      .get<Array<IMoodleEnv>>(
        `${apiUrls.moodle.moodleEnv}?filters[organization_id][id]=${organizationId}`,
      )
      .then((res) => {
        return res.data.length > 0 ? res.data[0] : undefined;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  },
);

export const generateMoodleEnv = createAsyncThunk(
  "profile/generateMoodleEnv",
  async ({ moodleUrl }: { moodleUrl?: string } & IAppLoaderAction) => {
    return await axios
      .post<{ message: string; moodleEnv: IMoodleEnv }>(`${apiUrls.moodle.generate}`, {
        moodleUrl,
      })
      .then((res) => {
        return res.data.moodleEnv;
      })
      .catch((error) => {
        console.error(error);
        return undefined;
      });
  },
);

export const revokeMoodleEnv = createAsyncThunk(
  "profile/revokeMoodleEnv",
  // eslint-disable-next-line no-empty-pattern
  async ({}: IAppLoaderAction) => {
    return await axios
      .post<{ message: string }>(`${apiUrls.moodle.revoke}`)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        console.error(error);
      });
  },
);

export const emptyOrganization = (id = 0): IOrganization => ({
  id,
  name: "",
  createdAt: "",
  updatedAt: "",
  legal_name: "",
  address_locality: "",
  address_region: "",
  address_country: "",
  phone: "",
  postal_code: "",
  email: "",
  siret: "",
  street_address: "",
  source_logo: {
    id: 0,
    s3_url: "",
    createdAt: "",
    updatedAt: "",
    organization: 0,
    type: "image",
    name: "",
    filename: "",
  },
  admin: {
    avatar3d_url: "",
    blocked: false,
    confirmationToken: null,
    confirmed: true,
    createdAt: "",
    email: "",
    firstname: "",
    has_accepted_cgv: true,
    id: 0,
    lastname: "",
    password: "",
    provider: null,
    resetPasswordToken: null,
    updatedAt: "",
    username: "",
  },
});

const applyPreferencesPatchToState = (state: IProfile, patch: IUserPreferencesPatch) => {
  if (state.currentUser) {
    state.currentUser.preferences = mergePatchObject(
      getUserPreferences({ currentUser: state.currentUser }),
      patch,
    );
  }
};

const setPreferencesInState = (state: IProfile, preferences: IUserPreferences) => {
  if (state.currentUser) {
    state.currentUser.preferences = preferences;
  }
};

const initialState: IProfile = {
  currentUser: undefined,
  organization: undefined,
  avatar3d: { status: "loading", preview: "" },
};

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfile: (state: IProfile) => {
      state.currentUser = initialState.currentUser;
      state.organization = initialState.organization;
      state.avatar3d = initialState.avatar3d;
      state.moodleEnv = undefined;
    },
    setProfile: (
      state: IProfile,
      action: PayloadAction<{ currentUser: IUser; organization: IOrganization }>,
    ) => {
      state.currentUser = action.payload.currentUser;
      state.organization = action.payload.organization;
    },
    setUserOrganization: (state: IProfile, action: PayloadAction<IOrganization>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.organization = { ...action.payload, admin: action.payload.admin };
      }
    },
    updateFirstname: (state: IProfile, action: PayloadAction<string>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.firstname = action.payload;
      }
    },
    updateLastname: (state: IProfile, action: PayloadAction<string>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.lastname = action.payload;
      }
    },
    updateAvatar3dUrl: (state: IProfile, action: PayloadAction<string>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.avatar3d_url = action.payload;
      }
    },
    updateEmail: (state: IProfile, action: PayloadAction<string>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.email = action.payload;
      }
    },
    setAvatar: (state: IProfile, action: PayloadAction<ISource>) => {
      if (state.currentUser !== undefined) {
        state.currentUser.source_avatar = { ...action.payload };
      }
    },
    updateUserAvatar: (state: IProfile, action: PayloadAction<ISource>) => {
      const bingo = (user: IUser) => Number(user.source_avatar.id) === Number(action.payload.id);
      if (state.currentUser) {
        if (bingo(state.currentUser)) {
          state.currentUser.source_avatar = action.payload;
        }
      }
    },
    updateOrganizationLogo: (state: IProfile, action: PayloadAction<ISource>) => {
      const bingo = (organization: IOrganization) =>
        Number(organization.source_logo.id) === Number(action.payload.id);
      if (state.organization) {
        if (bingo(state.organization)) {
          state.organization.source_logo = action.payload;
        }
      }
    },
    applyPreferencesPatch: (state: IProfile, action: PayloadAction<IUserPreferencesPatch>) => {
      applyPreferencesPatchToState(state, action.payload);
    },
    setPreferences: (state: IProfile, action: PayloadAction<IUserPreferences>) => {
      setPreferencesInState(state, action.payload);
    },
    resetActiveGptConversation: (state: IProfile) => {
      if (state.currentUser) {
        state.currentUser.active_gpt_conversation = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCurrentUserAndOrg.fulfilled, (state: IProfile, { payload }) => {
      state.organization = payload.organization;
      state.currentUser = payload.currentUser;
    });
    builder.addCase(updateUser.fulfilled, (state: IProfile, { payload, meta }) => {
      if (payload) {
        const keptPreferences = state.currentUser?.preferences;
        state.currentUser =
          "preferences" in meta.arg || keptPreferences === undefined
            ? payload
            : { ...payload, preferences: keptPreferences };
      }
    });
    builder.addCase(updateOrganization.fulfilled, (state: IProfile, { payload }) => {
      state.organization = payload;
    });
    builder.addCase(changeHasAcceptedCGV.fulfilled, (state: IProfile) => {
      if (state.currentUser) {
        state.currentUser.has_accepted_cgv = true;
      }
    });
    builder.addCase(fetchMoodleEnv.fulfilled, (state: IProfile, { payload }) => {
      state.moodleEnv = payload;
    });
    builder.addCase(generateMoodleEnv.fulfilled, (state: IProfile, { payload }) => {
      state.moodleEnv = payload;
    });
    builder.addCase(revokeMoodleEnv.fulfilled, (state: IProfile) => {
      state.moodleEnv = undefined;
    });
    builder.addCase(syncUserWithServer.fulfilled, (state: IProfile, { payload }) => {
      state.currentUser = payload;
    });
    builder.addCase(patchUserPreferences.pending, (state: IProfile, { meta }) => {
      applyPreferencesPatchToState(state, meta.arg);
    });
    builder.addCase(patchUserPreferences.fulfilled, (state: IProfile, { payload }) => {
      setPreferencesInState(state, payload);
    });
    builder.addCase(fetchUserPreferences.pending, (state: IProfile, { meta }) => {
      if (meta.arg.resync) {
        state.preferencesResyncing = true;
      }
    });
    builder.addCase(fetchUserPreferences.fulfilled, (state: IProfile, { meta }) => {
      if (meta.arg.resync) {
        state.preferencesResyncing = false;
      }
    });
    builder.addCase(fetchUserPreferences.rejected, (state: IProfile, { meta }) => {
      if (meta.arg.resync) {
        state.preferencesResyncing = false;
      }
    });
  },
});

export const profileReducer = profileSlice.reducer;

export const getCurrentUser = (state: RootState) => state.profile.currentUser;
export const getCurrentOrganization = (state: RootState) => state.profile.organization;
export const getIsAdminOfOrganization = (state: RootState) =>
  Number(state.profile.organization?.admin?.id) === Number(state.profile.currentUser?.id);

export const getCanManageLearners = (state: RootState) =>
  getIsAdminOfOrganization(state) || state.profile.currentUser?.role?.name === "Creator";

export const getActiveGptConversation = (state: RootState) =>
  state.profile.currentUser?.active_gpt_conversation;

export const getMoodleEnv = (state: RootState) => state.profile.moodleEnv;

export const getPreferencesResyncing = (state: RootState) =>
  state.profile.preferencesResyncing ?? false;

export const {
  clearProfile,
  setProfile,
  resetActiveGptConversation,
  applyPreferencesPatch,
  setPreferences,
} = profileSlice.actions;
